import * as React from 'react';

import URI from 'jsuri';

import API from 'js/lib/api';
import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import { Link, Typography2 } from '@coursera/cds-core';

import type { List, SalesforceLead } from 'bundles/enterprise-admin-contracts/utils/SalesforceAPI';
import SalesforceAPI from 'bundles/enterprise-admin-contracts/utils/SalesforceAPI';
import { EnterpriseLeadContext } from 'bundles/internal-enterprise/components/lead/EnterpriseLeadContainer';
import EnterpriseLeadTable from 'bundles/internal-enterprise/components/lead/EnterpriseLeadTable';
import Button from 'bundles/internal-ui/components/buttons/Button';
import Card from 'bundles/internal-ui/components/cards/Card';
import TextBox from 'bundles/internal-ui/components/forms/TextBox';
import Modal from 'bundles/internal-ui/components/modal/Modal';

import 'css!./__styles__/EnterpriseLeadSearch';

type Props = {};

const EnterpriseLeadSearch: React.FC<Props> = () => {
  const router = useRouter();
  const { setLead, updateInvoice, setPurchaseUserId } = React.useContext(EnterpriseLeadContext);

  const [salesforceLeads, setSalesforceLeads] = React.useState<SalesforceLead[]>([]);
  const [errorMsg, setErrorMsg] = React.useState<string>();
  const [query, setQuery] = React.useState('');
  const [isAuthDialogOpen, setIsAuthDialogOpen] = React.useState(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = React.useState(false);

  const handleAuthDialogClose = () => {
    setIsAuthDialogOpen(false);
  };

  const handleUpdateText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setQuery(value);
  };

  const updateInvoiceWithLead = (lead: SalesforceLead) => {
    updateInvoice({
      leadId: lead.id,
      company: lead.company,
      lastName: lead.lastName,
      firstName: lead.firstName,
      customerEmail: lead.email.toLowerCase(),
    });
  };

  const handleContinue = (lead: SalesforceLead) => {
    setLead(lead);
    updateInvoiceWithLead(lead);
    router.push(`/internal/enterprise/lead/invoice/${lead.id}`);
  };

  const handleCreateInvoiceManually = () => {
    const lead = {
      id: 'manual',
      company: '',
      email: '',
      lastName: '',
      firstName: '',
    };
    setLead(lead);
    updateInvoiceWithLead(lead);
    router.push(`/internal/enterprise/lead/invoice/${lead.id}`);
  };

  const handleCreateDiscountLinkManually = () => {
    const lead = {
      id: 'manual',
      company: '',
      email: '',
      lastName: '',
      firstName: '',
    };
    setLead(lead);
    updateInvoiceWithLead(lead);
    router.push(`/internal/enterprise/lead/cc-dd-link/${lead.id}`);
  };

  const showErrorDialog = (msg?: string) => {
    setIsErrorDialogOpen(true);
    setErrorMsg(msg || 'Unexpected error occurred.');
  };

  const handleImportError = (res: Response) => {
    if (res.status === 403 || res.status === 401 || res.status === 400) {
      // if 403 (unauthorized) or 400/401 (session expired or invalid)
      // Show auth dialog
      setIsAuthDialogOpen(true);
    } else if (res.status === 404) {
      showErrorDialog(`Matching lead does not exist for query ${query}.`);
    } else {
      showErrorDialog();
    }
  };

  const handleAuth = async () => {
    try {
      const url = await SalesforceAPI.getAuthorizeUrl();
      window.open(url, '_blank');
    } catch {
      showErrorDialog();
    } finally {
      setIsAuthDialogOpen(false);
    }
  };

  const handleUserNotFound = (email: string) => {
    showErrorDialog(
      `${email} is not affiliated with an account on Coursera. Please ensure the Admin has set up an account on Coursera before sending an invoice.`
    );
  };

  const handleErrorClose = () => {
    setIsErrorDialogOpen(false);
  };

  const handleSearchLead = () => {
    const q = query.trim().toLowerCase();

    // First, we confirm that the email is a valid coursera user

    const usersApi = API(`/api/users.v1`, { type: 'rest' });
    const uri = new URI()
      .addQueryParam('q', 'email')
      .addQueryParam('email', q)
      .addQueryParam('fields', ['id'].join(','));

    usersApi.get(uri.toString()).then((response) => {
      const users = response.elements;
      if (users.length === 0) {
        handleUserNotFound(q);
      } else {
        setPurchaseUserId(users[0].id);
        // If the user exists, query SFDC to get the leads
        SalesforceAPI.getLeadByEmail(q)
          .then((res: List<SalesforceLead>) => {
            setSalesforceLeads(res.elements);
          })
          .catch((res) => {
            handleImportError(res);
          });
      }
    });
  };

  const handleBack = () => {
    setSalesforceLeads([]);
    setQuery('');
  };

  const actions = [
    {
      icon: 'receipt',
      tooltip: 'Create Invoice',
      onClick: handleContinue,
    },
  ];

  const isSearchButtonDisabled = () => {
    return query.trim() === '';
  };

  return (
    <div className="rc-EnterpriseLeadSearch">
      <Card>
        <h3>Search leads in Salesforce</h3>

        <Typography2 component="p">
          Search for an existing lead in SFDC. Please ensure the following are correct before searching. For more
          information, please view the{' '}
          <Link href="https://docs.google.com/document/d/1w2cKnwGn1WYj6LnLuinWEqZ8xWj1A8wnC_MnCK4EUsg/edit#heading=h.l43as4nktg9z">
            Invoicing FAQ
          </Link>
          .
        </Typography2>
        <ul>
          <li>
            <Typography2 component="p">Ensure that your lead has been created in SFDC.</Typography2>
          </li>
          <li>
            <Typography2 component="p">
              Ensure that your lead has a Coursera account tied to the same SFDC lead email.
            </Typography2>
          </li>
          <li>
            <Typography2 component="p">
              Ensure that email id is in lower case. Any upper case letter will be converted to lower case by default.
            </Typography2>
          </li>
        </ul>

        {salesforceLeads.length > 0 ? (
          <div className="c-content">
            <p>Please confirm the lead to send invoice.</p>
            <EnterpriseLeadTable leads={salesforceLeads} actions={actions} />
            <Button text="Back" btnStyle="info" onClick={handleBack} />
          </div>
        ) : (
          <div className="c-content">
            <TextBox placeholder="Email" onUpdateField={handleUpdateText} currentValue={query} />
            <br />
            <Button
              text="Search lead by email"
              btnStyle="info"
              onClick={handleSearchLead}
              disabled={isSearchButtonDisabled()}
            />
          </div>
        )}

        {user.isSuperuserStrict() && (
          <div className="c-content superuser m-t-3 p-a-2">
            <div>(SUPERUSER only)</div>
            <Button text="Create discount link manually" btnStyle="info" onClick={handleCreateDiscountLinkManually} />
            <Button text="Create invoice manually" btnStyle="info" onClick={handleCreateInvoiceManually} />
          </div>
        )}
      </Card>
      <Modal
        title="Error"
        onRequestClose={handleErrorClose}
        open={isErrorDialogOpen}
        scrollable={false}
        showCloseIcon={true}
        actions={[
          {
            text: 'Close',
            onClick: handleErrorClose,
            style: 'default',
          },
        ]}
      >
        <div>{errorMsg}</div>
      </Modal>
      <Modal
        title="Authorize with Salesforce"
        onRequestClose={handleAuthDialogClose}
        open={isAuthDialogOpen}
        scrollable={false}
        showCloseIcon={true}
        actions={[
          {
            text: 'Authorize',
            onClick: handleAuth,
            style: 'primary',
          },
          {
            text: 'Cancel',
            onClick: handleAuthDialogClose,
            style: 'default',
          },
        ]}
      >
        <div className="vertical-box m-b-2">
          Please click the Authorize button and login with your Salesforce credentials. After successful login, return
          to this page and try again.
        </div>
      </Modal>
    </div>
  );
};

export default EnterpriseLeadSearch;
